/**
 * Detector de Formato de Archivo PTEL v1.0
 * 
 * FMT-01: Identifica el formato de origen del documento para aplicar
 * filtros de limpieza apropiados.
 * 
 * @module utils/formatDetector
 */

/** Tipos de formato detectables */
export type DocumentFormat = 
  | 'odt'        // LibreOffice Writer - requiere limpieza UTF-8 agresiva
  | 'markdown'   // Texto con formato Markdown (**bold**, *italic*)
  | 'html'       // HTML con tags
  | 'spreadsheet'// Excel/ODS - datos tabulares
  | 'plain';     // Texto plano limpio

/** Resultado del análisis de formato */
export interface FormatAnalysis {
  format: DocumentFormat;
  confidence: number;        // 0-100
  indicators: string[];      // Indicadores encontrados
  recommendations: string[]; // Filtros recomendados
}

/** Patrones para detectar Markdown */
const MARKDOWN_PATTERNS = {
  bold: /\*\*[^*]+\*\*/,           // **texto**
  italic: /(?<!\*)\*[^*]+\*(?!\*)/, // *texto* (no confundir con **)
  headers: /^#{1,6}\s+/m,          // # Header
  links: /\[([^\]]+)\]\([^)]+\)/,  // [texto](url)
  lists: /^[\s]*[-*+]\s+/m,        // - item
  code: /`[^`]+`/,                 // `código`
};

/** Patrones para detectar HTML */
const HTML_PATTERNS = {
  tags: /<\/?[a-z][^>]*>/i,
  entities: /&[a-z]+;|&#\d+;/i,
};

/** Patrones de corrupción UTF-8 típica de ODT */
const ODT_CORRUPTION_PATTERNS = [
  /Ã±/,      // ñ corrupta
  /Ã¡/,      // á corrupta
  /Ã©/,      // é corrupta
  /Ã­/,      // í corrupta
  /Ã³/,      // ó corrupta
  /Ãº/,      // ú corrupta
  /Â°/,      // º corrupto
  /Âª/,      // ª corrupta
  /â€"/,     // guión largo corrupto
  /â€œ|â€/,  // comillas tipográficas corruptas
];

/**
 * Detecta el formato de un texto/contenido
 */
export function detectFormat(content: string, fileName?: string): FormatAnalysis {
  const indicators: string[] = [];
  let format: DocumentFormat = 'plain';
  let confidence = 50;
  
  // 1. Detección por extensión de archivo (más fiable)
  if (fileName) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    
    if (ext === 'odt') {
      format = 'odt';
      confidence = 95;
      indicators.push(`extensión: .${ext}`);
    } else if (ext === 'md' || ext === 'markdown') {
      format = 'markdown';
      confidence = 95;
      indicators.push(`extensión: .${ext}`);
    } else if (ext === 'html' || ext === 'htm') {
      format = 'html';
      confidence = 95;
      indicators.push(`extensión: .${ext}`);
    } else if (['xlsx', 'xls', 'ods', 'csv'].includes(ext || '')) {
      format = 'spreadsheet';
      confidence = 95;
      indicators.push(`extensión: .${ext}`);
    }
  }
  
  // 2. Detección por contenido (complementa o sobrescribe si no hay extensión)
  if (confidence < 90) {
    // Detectar corrupción UTF-8 típica de ODT mal extraído
    const odtCorruptionCount = ODT_CORRUPTION_PATTERNS.filter(p => p.test(content)).length;
    if (odtCorruptionCount >= 2) {
      format = 'odt';
      confidence = 70 + odtCorruptionCount * 5;
      indicators.push(`corrupción UTF-8: ${odtCorruptionCount} patrones`);
    }
    
    // Detectar Markdown
    const markdownMatches = Object.entries(MARKDOWN_PATTERNS)
      .filter(([, pattern]) => pattern.test(content));
    
    if (markdownMatches.length >= 2) {
      // Si hay múltiples indicadores Markdown, es Markdown
      if (format !== 'odt' || markdownMatches.length >= 3) {
        format = 'markdown';
        confidence = 60 + markdownMatches.length * 10;
        indicators.push(...markdownMatches.map(([name]) => `markdown: ${name}`));
      }
    }
    
    // Detectar HTML
    if (HTML_PATTERNS.tags.test(content)) {
      const tagCount = (content.match(/<\/?[a-z][^>]*>/gi) || []).length;
      if (tagCount >= 3) {
        format = 'html';
        confidence = 70 + Math.min(tagCount, 10) * 3;
        indicators.push(`html: ${tagCount} tags`);
      }
    }
  }
  
  // 3. Generar recomendaciones de filtros
  const recommendations = getFilterRecommendations(format, indicators);
  
  return {
    format,
    confidence: Math.min(confidence, 100),
    indicators,
    recommendations,
  };
}

/**
 * Detecta formato desde un archivo File
 */
export function detectFormatFromFile(file: File): FormatAnalysis {
  // Por ahora solo usamos el nombre, el contenido se analiza después
  return detectFormat('', file.name);
}

/**
 * Analiza contenido de texto para refinar detección
 */
export function analyzeTextContent(text: string, baseAnalysis?: FormatAnalysis): FormatAnalysis {
  const analysis = detectFormat(text, undefined);
  
  if (baseAnalysis) {
    // Combinar análisis: priorizar extensión pero añadir indicadores de contenido
    return {
      format: baseAnalysis.confidence > analysis.confidence ? baseAnalysis.format : analysis.format,
      confidence: Math.max(baseAnalysis.confidence, analysis.confidence),
      indicators: [...baseAnalysis.indicators, ...analysis.indicators],
      recommendations: Array.from(new Set([...baseAnalysis.recommendations, ...analysis.recommendations])),
    };
  }
  
  return analysis;
}

/**
 * Genera recomendaciones de filtros según formato
 */
function getFilterRecommendations(format: DocumentFormat, indicators: string[]): string[] {
  const recommendations: string[] = [];
  
  switch (format) {
    case 'odt':
      recommendations.push('utf8Correction');      // Corregir mojibake
      recommendations.push('thousandsSeparator');  // Limpiar separadores de miles
      recommendations.push('placeholderRemoval');  // Eliminar "indicar", "---", etc.
      break;
      
    case 'markdown':
      recommendations.push('markdownCleaner');     // Limpiar **bold**, *italic*
      recommendations.push('linkRemoval');         // Eliminar [texto](url)
      break;
      
    case 'html':
      recommendations.push('htmlStripper');        // Eliminar tags HTML
      recommendations.push('entityDecoder');       // Decodificar &nbsp; etc.
      break;
      
    case 'spreadsheet':
      recommendations.push('thousandsSeparator');  // Excel usa puntos/comas
      recommendations.push('trimWhitespace');      // Espacios extra
      break;
      
    case 'plain':
      recommendations.push('trimWhitespace');
      break;
  }
  
  // Si hay corrupción UTF-8 detectada, siempre añadir corrección
  if (indicators.some(i => i.includes('UTF-8') || i.includes('corrupción'))) {
    if (!recommendations.includes('utf8Correction')) {
      recommendations.unshift('utf8Correction');
    }
  }
  
  return recommendations;
}

/**
 * Verifica si un texto tiene formato Markdown
 */
export function hasMarkdownFormatting(text: string): boolean {
  return MARKDOWN_PATTERNS.bold.test(text) || 
         MARKDOWN_PATTERNS.italic.test(text);
}

/**
 * Cuenta asteriscos de formato en un texto
 */
export function countMarkdownAsterisks(text: string): number {
  const boldMatches = text.match(/\*\*[^*]+\*\*/g) || [];
  const italicMatches = text.match(/(?<!\*)\*[^*]+\*(?!\*)/g) || [];
  return boldMatches.length + italicMatches.length;
}
